import { c } from 'ttag';

import { BadgeCategory } from '@freelancelabs/components';

/**
 * LE RÉFÉRENTIEL DES CODES D'ACTION DU JOURNAL D'AUDIT (PCG-337), et sa seule source.
 *
 * L'API rend le code d'action BRUT (`membership.role_changed`) et rien d'autre : elle
 * dit explicitement que le couple code -> famille -> libellé vit côté front. Ce fichier
 * est cet endroit, unique, d'où descendent la pastille de la table ET les options du
 * menu « Type d'action ». Deux sources donneraient deux libellés pour un même geste.
 *
 * **Trois faits par code, et ils ne se confondent pas :** la FAMILLE (sa teinte, un
 * fait de nature), le LIBELLÉ français (ce que le lecteur lit), et l'appartenance à la
 * LISTE CURÉE (est-ce un geste d'administration que cette vue trace).
 *
 * **`dashboard.embed_issued` est délibérément absent.** C'est l'émission d'un jeton
 * Metabase, du trafic opérationnel et non un geste d'administration : il ne doit ni
 * s'afficher ni être compté ici. L'absence du référentiel est le mécanisme. Comme
 * `actions` vide veut dire « toutes » pour l'API, l'écran envoie la LISTE CURÉE quand
 * aucun type n'est coché, faute de quoi le compteur inclurait ce trafic.
 *
 * **Un code inconnu ne se masque jamais en silence** : absent du référentiel, il tombe
 * dans la famille « Autre » (pastille neutre) et se rend avec son code brut pour
 * libellé. Un geste que l'API se met à écrire apparaît donc, lisiblement, sans qu'une
 * livraison front soit un préalable à ce qu'on le voie.
 */
export type ActionFamily = 'access' | 'role' | 'module' | 'dashboard' | 'mfa' | 'user' | 'partner' | 'admin' | 'other';

/**
 * La teinte d'une famille, portée par la catégorie de `Badge` (jetons `--badge-*` du
 * thème). « Autre » n'a pas de teinte à lui : un code inconnu ne doit pas emprunter la
 * couleur d'une famille qui n'est pas la sienne, il prend le gris neutre par défaut.
 */
const FAMILY_CATEGORY: Record<ActionFamily, BadgeCategory> = {
    access: 'access',
    role: 'role',
    module: 'module',
    dashboard: 'dashboard',
    mfa: 'mfa',
    user: 'user',
    partner: 'partner',
    admin: 'admin',
    other: 'default',
};

/**
 * Code brut -> famille. L'ORDRE des clés est l'ordre du menu (celui de la maquette du
 * back-office, l'administration en dernier) : `Object.keys` le conserve, donc la liste
 * curée et les options du filtre en descendent déjà triées, familles groupées.
 */
const CODE_FAMILY: Record<string, ActionFamily> = {
    'organization.portal_opened': 'access',
    'organization.portal_closed': 'access',

    'membership.created': 'role',
    'membership.activated': 'role',
    'membership.role_changed': 'role',
    'membership.revoked': 'role',
    'membership.reinstated': 'role',
    'membership.markets_updated': 'role',

    'organization.module_enabled': 'module',
    'organization.module_disabled': 'module',

    'dashboard.declared': 'dashboard',
    'dashboard.updated': 'dashboard',
    'dashboard.archived': 'dashboard',
    'dashboard.granted': 'dashboard',
    'dashboard.revoked': 'dashboard',

    'account.mfa_required': 'mfa',
    'account.mfa_not_required': 'mfa',

    'invitation.created': 'user',
    'invitation.sent': 'user',
    'invitation.resent': 'user',
    'invitation.accepted': 'user',
    'contact.onboarded': 'user',

    'partner_list.declared': 'partner',
    'partner_list.updated': 'partner',
    'partner_list.display_changed': 'partner',
    'partner_list.partners_added': 'partner',
    'partner_list.partner_removed': 'partner',
    'partner_list.archived': 'partner',

    'collaborator.created': 'admin',
    'collaborator.bootstrapped': 'admin',
    'collaborator.portfolio_updated': 'admin',
    'need_submission.created': 'admin',
    'role.created': 'admin',
};

/**
 * La liste des codes que cette vue trace, à envoyer dans `actions` quand aucun type
 * n'est coché. Curée, donc `dashboard.embed_issued` n'en fait pas partie. C'est ce qui
 * borne le compteur « N événements » aux seuls gestes d'administration.
 */
export const CURATED_ACTION_CODES = Object.keys(CODE_FAMILY);

/** La famille d'un code, « Autre » quand le référentiel ne le connaît pas. */
export const familyOf = (code: string): ActionFamily => CODE_FAMILY[code] ?? 'other';

/** La catégorie de pastille (teinte) d'un code. */
export const badgeCategoryOf = (code: string): BadgeCategory => FAMILY_CATEGORY[familyOf(code)];

/**
 * Le libellé français d'un code. Un code inconnu se rend AVEC SON CODE BRUT plutôt
 * qu'avec une phrase inventée : c'est honnête, et c'est cherchable.
 *
 * Les libellés se calculent à l'appel (au rendu) et non au chargement du module : le
 * catalogue i18n peut n'être pas encore posé à l'import, et une chaîne figée là
 * resterait en anglais.
 */
export const actionLabel = (code: string): string => {
    switch (code) {
        case 'organization.portal_opened':
            return c('Activity log action').t`Portal access opened`;
        case 'organization.portal_closed':
            return c('Activity log action').t`Portal access closed`;
        case 'membership.created':
            return c('Activity log action').t`Membership created`;
        case 'membership.activated':
            return c('Activity log action').t`Membership activated`;
        case 'membership.role_changed':
            return c('Activity log action').t`Role changed`;
        case 'membership.revoked':
            return c('Activity log action').t`Access revoked`;
        case 'membership.reinstated':
            return c('Activity log action').t`Access reinstated`;
        case 'membership.markets_updated':
            return c('Activity log action').t`Scope updated`;
        case 'organization.module_enabled':
            return c('Activity log action').t`Module opened`;
        case 'organization.module_disabled':
            return c('Activity log action').t`Module closed`;
        case 'dashboard.declared':
            return c('Activity log action').t`Dashboard declared`;
        case 'dashboard.updated':
            return c('Activity log action').t`Dashboard updated`;
        case 'dashboard.archived':
            return c('Activity log action').t`Dashboard archived`;
        case 'dashboard.granted':
            return c('Activity log action').t`Dashboard granted`;
        case 'dashboard.revoked':
            return c('Activity log action').t`Dashboard revoked`;
        case 'account.mfa_required':
            return c('Activity log action').t`Two-factor authentication required`;
        case 'account.mfa_not_required':
            return c('Activity log action').t`Two-factor authentication no longer required`;
        case 'invitation.created':
            return c('Activity log action').t`Invitation created`;
        case 'invitation.sent':
            return c('Activity log action').t`Invitation sent`;
        case 'invitation.resent':
            return c('Activity log action').t`Invitation resent`;
        case 'invitation.accepted':
            return c('Activity log action').t`Invitation accepted`;
        case 'contact.onboarded':
            return c('Activity log action').t`Contact onboarded`;
        case 'partner_list.declared':
            return c('Activity log action').t`Partner list declared`;
        case 'partner_list.updated':
            return c('Activity log action').t`Partner list updated`;
        case 'partner_list.display_changed':
            return c('Activity log action').t`Partner list display changed`;
        case 'partner_list.partners_added':
            return c('Activity log action').t`Partners added`;
        case 'partner_list.partner_removed':
            return c('Activity log action').t`Partner removed`;
        case 'partner_list.archived':
            return c('Activity log action').t`Partner list archived`;
        case 'collaborator.created':
            return c('Activity log action').t`Collaborator created`;
        case 'collaborator.bootstrapped':
            return c('Activity log action').t`Collaborator bootstrapped`;
        case 'collaborator.portfolio_updated':
            return c('Activity log action').t`Portfolio updated`;
        case 'need_submission.created':
            return c('Activity log action').t`Need request created`;
        case 'role.created':
            return c('Activity log action').t`Role created`;
        default:
            // Le code brut, jamais une phrase inventée pour un geste qu'on ne nomme pas.
            return code;
    }
};

export interface ActionDescriptor {
    family: ActionFamily;
    badgeCategory: BadgeCategory;
    label: string;
}

/** Ce qu'il faut pour rendre une ligne : la teinte et le libellé, résolus ensemble. */
export const describeAction = (code: string): ActionDescriptor => {
    const family = familyOf(code);
    return { family, badgeCategory: FAMILY_CATEGORY[family], label: actionLabel(code) };
};
